import validator from "validator";

import { clientTypeOptions } from "../../fixtures/clientTypes";
import { validateEmailFull } from "./emailValidation";

/**
 * Legacy email validator for Ant Design Form compatibility
 * Now uses the enhanced validation logic with disposable and role-based detection
 */
export const validateEmail = async (_: any, value: any): Promise<void> => {
  if (!value || value.trim() === "") {
    return Promise.resolve();
  }

  const result = await validateEmailFull(value, {
    required: false,
    checkDNS: true,
    checkDisposable: true,
    checkRoleBased: true,
  });

  // For blocking errors (invalid, disposable, etc.)
  if (!result.isValid && result.errorMessage) {
    return Promise.reject(new Error(result.errorMessage));
  }

  // For role-based emails, we allow but don't show warning in legacy forms
  // The Ant Design form would need custom handling to show warnings
  // The warning message is still in result.errorMessage if needed

  return Promise.resolve();
};

/**
 * Validates that the email is not the advisor's (user's) primary or secondary email
 * @param userData - The user/advisor data from Redux state
 * @param value - The email value to validate
 * @returns Promise that resolves if valid, rejects if email matches advisor's email
 */
export const validateEmailNotAdvisor = (userData: any, value: any) => {
  if (!value || value.trim() === "") {
    return Promise.resolve();
  }

  const trimmedValue = value.trim().toLowerCase();
  const advisorPrimaryEmail = userData?.email?.toLowerCase();
  const advisorSecondaryEmail = userData?.optional_email?.toLowerCase();

  if (
    (advisorPrimaryEmail && trimmedValue === advisorPrimaryEmail) ||
    (advisorSecondaryEmail && trimmedValue === advisorSecondaryEmail)
  ) {
    return Promise.reject(
      new Error("No puedes usar tu propio correo electrónico"),
    );
  }

  return Promise.resolve();
};

/**
 * Validates that the phone number is not the advisor's (user's) phone number
 * @param userData - The user/advisor data from Redux state
 * @param value - The phone number value to validate
 * @returns Promise that resolves if valid, rejects if phone number matches advisor's phone
 */
export const validatePhoneNotAdvisor = (userData: any, value: any) => {
  if (!value || value.trim() === "") {
    return Promise.resolve();
  }

  const trimmedValue = value.trim();
  const advisorPhone = userData?.phone_number?.toString();

  if (advisorPhone && trimmedValue === advisorPhone) {
    return Promise.reject(new Error("No puedes utilizar tu propio número"));
  }

  return Promise.resolve();
};

export const validateCURP = (_: any, value: any) => {
  const curpRegex =
    /^([A-Z][AEIOUX][A-Z]{2}[0-9]{2}(?:0[1-9]|1[012])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|B[CS]|C[CLMSH]|D[FG]|G[TR]|HG|JC|M[CNS]|N[ETL]|OC|PL|Q[TR]|S[PLR]|T[CSL]|VZ|YN|ZS)[B-DF-HJ-NP-TV-Z]{3}[A-Z\d]{1})([\d]{1})$/;
  if (value && !curpRegex.test(value)) {
    return Promise.reject(new Error("Favor de proporcionar un CURP válido"));
  }
  return Promise.resolve();
};

export const validateRFC = (_: any, value: any) => {
  const rfcRegex =
    /^([A-ZÑ\x26]{4}([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1]))([A-Z\d]{2}([A]|[0-9]){1})?$/;
  if (value && !rfcRegex.test(value)) {
    return Promise.reject(new Error("Favor de proporcionar un RFC válido"));
  }
  return Promise.resolve();
};

export const validateCompanyRFC = (_: any, value: any) => {
  const rfcRegex =
    /^([A-ZÑ\x26]{3}([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1]))([A-Z\d]{2}([A]|[0-9]){1})?$/;
  if (value && !rfcRegex.test(value)) {
    return Promise.reject(new Error("Favor de proporcionar un RFC válido"));
  }
  return Promise.resolve();
};

export const validatePhoneNumber = (_: any, value: any) => {
  if (!value) return Promise.resolve();
  const phoneRegExp = /^\d{10}$/; // Regular expression for 10-digit phone number
  if (!value || !phoneRegExp.test(value)) {
    return Promise.reject(
      new Error("Favor de ingresar un número de 10 dígitos"),
    );
  }
  return Promise.resolve();
};

export const validateBirthDate = (_: any, value: any) => {
  const selectedDate = new Date(value);
  const currentYear = new Date().getFullYear();

  if (selectedDate.getFullYear() < 1900) {
    return Promise.reject(
      new Error("La fecha de nacimiento debe ser posterior a 1899"),
    );
  }

  if (currentYear - selectedDate.getFullYear() < 18) {
    return Promise.reject(new Error("La persona debe de ser mayor de 18 años"));
  }

  return Promise.resolve();
};

export const validateZipCode = (_: any, value: any) => {
  const zipCodeRegExp = /^\d{5}$/; // Regular expression for 10-digit phone number
  if (!value || !zipCodeRegExp.test(value)) {
    return Promise.reject(
      new Error("Favor de ingresar un código postal de 5 dígitos"),
    );
  }
  return Promise.resolve();
};

export const validateCheckbox = (_: any, value: boolean) => {
  if (!value) {
    return Promise.reject(new Error("Este campo es requerido"));
  }
  return Promise.resolve();
};

export const validateClientType = (_: any, value: any) => {
  const clientTypes: string[] = clientTypeOptions.map(
    (clientType) => clientType.value,
  );
  if (!clientTypes.includes(value)) {
    return Promise.reject(
      new Error("Favor de seleccionar un tipo de cliente válido"),
    );
  }
  return Promise.resolve();
};

export const passwordRules = [
  {
    pattern: new RegExp("(?=.*[a-z])"),
    message: "La contraseña debe contener al menos una minúscula",
  },
  {
    pattern: new RegExp("(?=.*[A-Z])"),
    message: "La contraseña debe contener al menos una mayúscula",
  },
  {
    pattern: new RegExp("(?=.*[0-9])"),
    message: "La contraseña debe contener al menos un número",
  },
  {
    pattern: new RegExp("(?=.*[!\"#$%&/()='?¿¡@+*~{}[^<>,,.:_-])"),
    message: "La contraseña debe contener al menos un caracter especial",
  },
  {
    pattern: new RegExp(".{8,}"),
    message: "La contraseña debe contener al menos 8 caractéres",
  },
];
